import { useState, useRef, useEffect } from 'react'
import { Pencil, Check, X, Loader2 } from 'lucide-react'
import { checkNameAvailable, renamePlayer } from '../utils/api'

interface NameEditorProps {
  currentName: string
  password: string
  onRenamed: (newName: string) => void
  className?: string
  textClassName?: string
}

export default function NameEditor({ currentName, password, onRenamed, className = '', textClassName = '' }: NameEditorProps) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(currentName)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setName(currentName)
  }, [currentName])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) { setError('Name cannot be empty'); return }
    if (trimmed === currentName) { setEditing(false); return }

    setSaving(true)
    setError('')

    try {
      const available = await checkNameAvailable(trimmed, password)
      if (!available) {
        setError('Name already taken')
        setSaving(false)
        return
      }

      await renamePlayer(password, trimmed)
      localStorage.setItem('hsk2-player-name', trimmed)
      onRenamed(trimmed)
      setEditing(false)
    } catch (e: any) {
      setError(e.message || 'Failed to rename')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setName(currentName)
    setError('')
    setEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave()
    if (e.key === 'Escape') handleCancel()
  }

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className={`flex items-center gap-1.5 group ${className}`}
      >
        <span className={textClassName}>{currentName}</span>
        <Pencil className="w-3.5 h-3.5 text-white/50 group-hover:text-white/80 transition-colors" />
      </button>
    )
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => { setName(e.target.value); setError('') }}
          onKeyDown={handleKeyDown}
          maxLength={20}
          className="bg-white/90 text-gray-800 rounded-lg px-2 py-1 text-sm font-bold
                     focus:outline-none focus:ring-2 focus:ring-yellow-400 w-32"
        />
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-green-500 rounded-lg p-1 active:scale-95 transition-transform"
        >
          {saving ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Check className="w-4 h-4 text-white" />}
        </button>
        <button
          onClick={handleCancel}
          className="bg-white/20 rounded-lg p-1 active:scale-95 transition-transform"
        >
          <X className="w-4 h-4 text-white" />
        </button>
      </div>
      {error && <p className="text-red-300 text-[10px] mt-0.5">{error}</p>}
    </div>
  )
}
